<script setup lang="ts">
import { TabsContent, TabsIndicator, TabsList, TabsRoot, TabsTrigger } from 'reka-ui'

import EIcon from './EIcon.vue'
import type { TabDefinition } from './types'

/**
 * Tabs for document sections (Lines / Taxes / Attachments / Audit) and for the
 * right rail. Activation is `manual`: arrow keys move focus, Enter or Space
 * activates. That matters here because a tab panel may load data, and automatic
 * activation would fire a request for every panel a keyboard user passes over.
 */

withDefaults(
  defineProps<{
    tabs: readonly TabDefinition[]
    /** `pill` for compact in-panel switches, `underline` for page sections. */
    variant?: 'underline' | 'pill'
    label?: string
  }>(),
  { variant: 'underline', label: '' },
)

const model = defineModel<string>({ required: true })
</script>

<template>
  <TabsRoot v-model="model" class="e-tabs" activation-mode="manual">
    <TabsList
      class="e-tabs__list"
      :class="`e-tabs__list--${variant}`"
      :aria-label="label === '' ? undefined : label"
    >
      <TabsIndicator v-if="variant === 'underline'" class="e-tabs__indicator" />
      <TabsTrigger
        v-for="tab in tabs"
        :key="tab.value"
        :value="tab.value"
        :disabled="tab.disabled ?? false"
        class="e-tabs__trigger"
      >
        <EIcon v-if="tab.icon" :name="tab.icon" :size="14" />
        <span>{{ tab.label }}</span>
        <span v-if="tab.count !== undefined" class="e-tabs__count">{{ tab.count }}</span>
      </TabsTrigger>
    </TabsList>

    <TabsContent v-for="tab in tabs" :key="tab.value" :value="tab.value" class="e-tabs__panel">
      <slot :name="tab.value" />
    </TabsContent>
  </TabsRoot>
</template>

<style>
.e-tabs {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.e-tabs__list {
  position: relative;
  display: flex;
  align-items: center;
  gap: var(--erpx-space-1);
  min-width: 0;
  overflow-x: auto;
  scrollbar-width: none;
}

.e-tabs__list::-webkit-scrollbar {
  display: none;
}

.e-tabs__list--underline {
  border-block-end: 1px solid var(--erpx-color-border-subtle);
}

.e-tabs__list--pill {
  padding: 2px;
  background-color: var(--erpx-color-surface-sunken);
  border: 1px solid var(--erpx-color-border-subtle);
  border-radius: var(--erpx-radius);
  align-self: flex-start;
}

.e-tabs__trigger {
  display: inline-flex;
  align-items: center;
  gap: var(--erpx-space-2);
  height: var(--erpx-control-h-lg);
  padding-inline: var(--erpx-space-3);
  background: none;
  border: none;
  color: var(--erpx-color-text-muted);
  font-family: inherit;
  font-size: var(--erpx-text-sm);
  font-weight: var(--erpx-weight-medium);
  white-space: nowrap;
  cursor: pointer;
  border-radius: var(--erpx-radius-sm);
  transition: color var(--erpx-duration-fast) var(--erpx-easing-standard);
}

.e-tabs__trigger:hover:not([data-disabled]) {
  color: var(--erpx-color-text-primary);
}

.e-tabs__trigger:focus-visible {
  outline: none;
  box-shadow: var(--erpx-focus-ring);
}

.e-tabs__trigger[data-state='active'] {
  color: var(--erpx-color-text-primary);
}

.e-tabs__list--pill .e-tabs__trigger[data-state='active'] {
  background-color: var(--erpx-color-surface-raised);
  box-shadow: var(--erpx-shadow-1);
}

.e-tabs__trigger[data-disabled] {
  color: var(--erpx-color-text-disabled);
  cursor: not-allowed;
}

.e-tabs__count {
  min-width: 18px;
  padding-inline: 5px;
  background-color: var(--erpx-color-neutral-surface);
  border-radius: var(--erpx-radius-full);
  color: var(--erpx-color-neutral-text);
  font-family: var(--erpx-font-mono);
  font-size: var(--erpx-text-2xs);
  line-height: 16px;
  text-align: center;
}

.e-tabs__indicator {
  position: absolute;
  inset-block-end: -1px;
  height: 2px;
  width: var(--reka-tabs-indicator-size);
  transform: translateX(var(--reka-tabs-indicator-position));
  background-color: var(--erpx-color-accent-solid);
  transition:
    width var(--erpx-duration-normal) var(--erpx-easing-standard),
    transform var(--erpx-duration-normal) var(--erpx-easing-standard);
}

.e-tabs__panel {
  padding-block-start: var(--erpx-space-4);
  min-width: 0;
}

.e-tabs__panel:focus-visible {
  outline: none;
  box-shadow: var(--erpx-focus-ring);
}
</style>
